import React, { useEffect, useState } from 'react';
import { AccountLevel, AccountLevels, UserResponse } from '../../models/admin';
import {
  block,
  unblock,
  listUsers,
  remove,
  updateAccount
} from '../../services/admin.service';
import LoadingSpinner from '../../components/LoadingSpinner';
import DeleteButton from '../../components/DeleteButton';
import Feedback from '../../components/Feedback';
import { ReactComponent as CheckIcon } from '../../assets/check.svg';
import { ReactComponent as CloseIcon } from '../../assets/close_medium.svg';
import { ReactComponent as EditIcon } from '../../assets/edit_large.svg';
import Button from '../../components/Button';

const Users: React.FC = () => {
  const [users, setUsers] = useState([] as UserResponse[]);
  const [errors, setErrors] = useState([] as string[]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteLoading, setDeleteLoading] = useState([] as string[]);
  const [blockLoading, setBlockLoading] = useState([] as string[]);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<AccountLevel>('Free');
  const [accountLoading, setAccountLoading] = useState([] as string[]);

  const getUsers = async () => {
    try {
      if (loading) {
        return;
      }
      setLoading(true);

      setErrors([]);
      const result = await listUsers();

      if (result.success && result.data) {
        setUsers(result.data.users || []);
      } else {
        setErrors(result.errors);
      }
      setLoading(false);
    } catch {
      setErrors(['There was a problem loading the errors']);
    }
  };

  const deleteUser = async (userId: string) => {
    if (deleteLoading.includes(userId)) {
      return;
    }

    setDeleteLoading((p) => [...p, userId]);

    const response = await remove(userId);
    if (response.success) {
      setMessage('User deleted');
      setErrors([]);
      await getUsers();
    } else {
      setMessage('');
      setErrors(response.errors);
    }

    setDeleteLoading((p) => p.filter((id) => id !== userId));
  };

  const blockUser = async (userId: string, unblockUser: boolean) => {
    if (blockLoading.includes(userId)) {
      return;
    }

    setBlockLoading((p) => [...p, userId]);

    const response = unblockUser ? await unblock(userId) : await block(userId);
    if (response.success) {
      setMessage(unblockUser ? 'User unblocked' : 'User blocked');
      setErrors([]);
      await getUsers();
    } else {
      setMessage('');
      setErrors(response.errors);
    }

    setBlockLoading((p) => p.filter((id) => id !== userId));
  };

  const saveAccountLevel = async (userId: string) => {
    if (accountLoading.includes(userId)) {
      return;
    }

    setAccountLoading((p) => [...p, userId]);

    const response = await updateAccount(userId, editValue);

    if (response.success) {
      setMessage('Account level updated');
      setErrors([]);
      setEditUserId(null);
      await getUsers();
    } else {
      setMessage('');
      setErrors(response.errors);
    }

    setAccountLoading((p) => p.filter((id) => id !== userId));
  };

  useEffect(() => {
    getUsers();
  }, []);

  return (
    <>
      <h2>Users</h2>
      <p>Here are all registered users</p>

      {loading && <LoadingSpinner />}

      {!loading && (
        <div className="content-page">
          {users.length === 0 && errors.length === 0 && <p>No users found</p>}
          <Feedback messages={errors} type="error" />
          <Feedback message={message} type="success" />
          {users.length > 0 && (
            <table className="content-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email Address</th>
                  <th>Account Level</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((p) => (
                  <tr key={p.emailAddress}>
                    <td>
                      {p.firstName} {p.lastName}
                    </td>
                    <td>{p.emailAddress}</td>
                    <td className="editor">
                      {editUserId === p.userId ? (
                        <form className="content-form-inline">
                          <select
                            value={editValue}
                            onChange={(e) =>
                              setEditValue(e.target.value as AccountLevel)
                            }
                          >
                            {AccountLevels.map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                          <Button
                            isIcon
                            isLoading={accountLoading.includes(p.userId)}
                            onClick={() => saveAccountLevel(p.userId)}
                            title="Save"
                            aria-label="Save"
                          >
                            <CheckIcon />
                          </Button>
                          <Button
                            isIcon
                            onClick={() => setEditUserId(null)}
                            title="Cancel"
                            aria-label="Cancel"
                          >
                            <CloseIcon />
                          </Button>
                        </form>
                      ) : (
                        <div>
                          <Button
                            isIcon
                            onClick={() => {
                              setEditUserId(p.userId);
                              setEditValue(p.accountLevel as AccountLevel);
                            }}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <EditIcon />
                          </Button>
                          <span>{p.accountLevel}</span>
                        </div>
                      )}
                    </td>
                    <td className="button-container">
                      <DeleteButton
                        isLoading={deleteLoading.includes(p.userId)}
                        onClick={() => deleteUser(p.userId)}
                        message="Are you sure you want to delete this user?  Note that this cannot be undone."
                      >
                        Delete
                      </DeleteButton>
                      {p.manualBlockDate ? (
                        <DeleteButton
                          isLoading={blockLoading.includes(p.userId)}
                          onClick={() => blockUser(p.userId, true)}
                          message="Are you sure you want to unblock this user?  This user will then have access."
                        >
                          Unblock
                        </DeleteButton>
                      ) : (
                        <DeleteButton
                          isLoading={blockLoading.includes(p.userId)}
                          onClick={() => blockUser(p.userId, false)}
                          message="Are you sure you want to block this user?  This user will not have access."
                        >
                          Lock
                        </DeleteButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
};

export default Users;
